import { StateProvider, VisibilityProvider } from "@json-render/react";
import { environmentManager, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { useDocumentTitle } from "usehooks-ts";

import type { StudyOSUiMessage } from "@/agents";
import { ChatContextProvider } from "@/components/chat-panel/chat-context-provider";
import { SuspendedChatData } from "@/components/chat-panel/suspended-data";
import { ErrorPage } from "@/components/error-page";
import { suspenseThreadQueryOptions, threadQueryOptions } from "@/hooks/query/thread";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

import { ChatLayout } from "./-components/chat-layout";
import { ThreadSkeleton } from "./-components/thread-skeleton";

export const Route = createFileRoute("/(app)/chat/$threadId")({
	component: RouteComponent,
	pendingComponent: ThreadSkeleton,
	errorComponent: ErrorPage,
	wrapInSuspense: true,
	beforeLoad: async ({ params, context }) => {
		if (environmentManager.isServer()) {
			const query = threadQueryOptions({
				threadId: params.threadId,
			});
			const thread = await context.queryClient.ensureQueryData(query);
			console.log("thread", { thread });

			return {
				threadTitle: thread.title,
			};
		}

		return {
			threadTitle: "New Conversation",
		};
	},
	loader: async ({ context }) => {
		return {
			threadTitle: context.threadTitle,
		};
	},
	head: ({ loaderData, match }) =>
		noIndexSeo({
			title: loaderData?.threadTitle
				? `${loaderData.threadTitle} | StudyOS`
				: m.chat_defaultTitle(),
			description: m.chat_description(),
			pathname: match.pathname,
		}),
});

function RouteComponent() {
	const { threadId } = Route.useParams();
	const { data: thread } = useSuspenseQuery(suspenseThreadQueryOptions({ threadId }));

	useDocumentTitle(thread.title ? `${thread.title} | StudyOS` : m.chat_defaultTitle());

	const initialMessages = useMemo(() => {
		return thread.messages.map((msg) => ({
			id: msg.id,
			role: msg.role as "user" | "assistant",
			parts: msg.parts,
			createdAt: new Date(msg.createdAt),
			metadata: msg.metadata,
		})) as unknown as StudyOSUiMessage[];
	}, [thread.messages]);

	return (
		<ChatContextProvider threadId={threadId} initialMessages={initialMessages}>
			<Suspense fallback={<ThreadSkeleton />}>
				<SuspendedChatData threadId={threadId}>
					<StateProvider>
						<VisibilityProvider>
							<ChatLayout threadId={threadId} />
						</VisibilityProvider>
					</StateProvider>
				</SuspendedChatData>
			</Suspense>
		</ChatContextProvider>
	);
}
